import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  PaperPlaneTilt, Plus, EnvelopeSimple, CaretDown, CaretUp,
  PencilSimple, Trash, AirplaneTilt, Anchor, ArrowUp, ArrowDown,
  Clock, CheckCircle, Gear, Info, WarningCircle
} from '@phosphor-icons/react';

const API = '/api';

const TYPES = [
  { value: 'notification_to_state', label: 'Notification to State' },
  { value: 'request_for_information', label: 'Request for Information' },
  { value: 'interim_report', label: 'Interim Report' },
  { value: 'safety_recommendation', label: 'Safety Recommendation' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'response_received', label: 'Response Received' },
  { value: 'internal_memo', label: 'Internal Memo' },
  { value: 'witness_communication', label: 'Witness Communication' },
  { value: 'regulatory_notice', label: 'Regulatory Notice' },
  { value: 'other', label: 'Other' },
];

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-emerald-100 text-emerald-800',
  logged: 'bg-blue-100 text-blue-800',
};

const emptyForm = {
  investigation_id: '',
  type: 'other',
  subject: '',
  body: '',
  to_recipients: '',
  cc_recipients: '',
  direction: 'outgoing',
};

function CorrCard({ item, onEdit, onSend, onDelete, canEdit }) {
  const [expanded, setExpanded] = useState(false);
  const isIncoming = item.direction === 'incoming';

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-sm transition-shadow" data-testid={`corr-card-${item.id}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-5 py-3 text-left"
        data-testid={`toggle-corr-${item.id}`}
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${isIncoming ? 'bg-green-100' : 'bg-blue-100'}`}>
          {isIncoming ? <ArrowDown size={16} className="text-green-600" /> : <ArrowUp size={16} className="text-blue-600" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-xs font-bold text-slate-500">{item.ref_number}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[item.status] || STATUS_STYLES.draft}`}>{item.status}</span>
            <span className="px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">{TYPES.find(t => t.value === item.type)?.label || item.type}</span>
          </div>
          <p className="text-sm font-medium text-slate-800 truncate mt-0.5">{item.subject || '(No subject)'}</p>
        </div>
        <div className="text-right shrink-0">
          <span className="text-xs text-slate-400 block">{item.investigation_number}</span>
          <span className="text-xs text-slate-400">{item.created_at ? new Date(item.created_at).toLocaleDateString() : ''}</span>
        </div>
        {expanded ? <CaretUp size={14} className="text-slate-400 shrink-0" /> : <CaretDown size={14} className="text-slate-400 shrink-0" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-100 px-5 py-4 space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div><span className="text-xs text-slate-500 block">To</span><span className="font-medium text-slate-800 break-all">{item.to_recipients || '-'}</span></div>
            <div><span className="text-xs text-slate-500 block">CC</span><span className="font-medium text-slate-800 break-all">{item.cc_recipients || '-'}</span></div>
            <div><span className="text-xs text-slate-500 block">From</span><span className="font-medium text-slate-800">{item.from_name || item.from_email || '-'}</span></div>
          </div>

          {item.body && (
            <div>
              <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Body</h4>
              <div className="text-sm text-slate-700 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap max-h-48 overflow-y-auto">{item.body}</div>
            </div>
          )}

          {item.email_result && (
            <div className={`text-xs rounded-lg p-2 ${item.email_result.sent ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
              <Info size={12} className="inline mr-1" />
              {item.email_result.sent
                ? `Sent via ${item.email_result.provider}${item.email_result.message_id ? ` (ID: ${item.email_result.message_id})` : ''}`
                : `${item.email_result.note || `Logged via ${item.email_result.provider}`}`}
            </div>
          )}

          {item.sent_at && (
            <div className="text-xs text-slate-400">
              Sent: {new Date(item.sent_at).toLocaleString()} by {item.sent_by}
            </div>
          )}

          <div className="flex gap-2 pt-2 border-t border-slate-100">
            {canEdit && item.status === 'draft' && (
              <>
                <Button variant="outline" size="sm" className="rounded-lg" onClick={() => onEdit(item)} data-testid={`edit-corr-${item.id}`}>
                  <PencilSimple size={14} className="mr-1" /> Edit
                </Button>
                <Button size="sm" className="rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white" onClick={() => onSend(item.id)} data-testid={`send-corr-${item.id}`}>
                  <PaperPlaneTilt size={14} className="mr-1" /> Send
                </Button>
              </>
            )}
            {canEdit && (
              <Button variant="outline" size="sm" className="rounded-lg text-red-500 hover:bg-red-50" onClick={() => onDelete(item.id)} data-testid={`delete-corr-${item.id}`}>
                <Trash size={14} className="mr-1" /> Delete
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Correspondence() {
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'iic';
  const isAdmin = user?.role === 'admin';

  const [items, setItems] = useState([]);
  const [investigations, setInvestigations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [emailConfig, setEmailConfig] = useState(null);
  const [showConfig, setShowConfig] = useState(false);
  const [filterInv, setFilterInv] = useState('all');
  const [filterDir, setFilterDir] = useState('all');

  const loadData = useCallback(async () => {
    try {
      const [corrRes, invRes] = await Promise.all([
        axios.get(`${API}/correspondence`, { withCredentials: true }),
        axios.get(`${API}/investigations`, { withCredentials: true })
      ]);
      setItems(corrRes.data);
      setInvestigations(invRes.data);
      if (isAdmin) {
        try {
          const cfgRes = await axios.get(`${API}/correspondence/email-config`, { withCredentials: true });
          setEmailConfig(cfgRes.data);
        } catch (e) { /* non-admin */ }
      }
    } catch (e) { /* error handled silently */ } finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/correspondence/${editingId}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/correspondence`, formData, { withCredentials: true });
      }
      setIsFormOpen(false);
      setEditingId(null);
      setFormData({ ...emptyForm });
      loadData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  const handleSend = async (id) => {
    if (!window.confirm('Send this correspondence?')) return;
    try {
      const res = await axios.post(`${API}/correspondence/${id}/send`, {}, { withCredentials: true });
      if (res.data.email_result?.sent) {
        alert('Email sent successfully!');
      } else {
        alert(`Email logged. ${res.data.email_result?.note || 'Configure an email provider in Settings to enable actual sending.'}`);
      }
      loadData();
    } catch (e) { alert(e.response?.data?.detail || 'Send failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this correspondence?')) return;
    try {
      await axios.delete(`${API}/correspondence/${id}`, { withCredentials: true });
      loadData();
    } catch (e) { alert(e.response?.data?.detail || 'Delete failed'); }
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      investigation_id: item.investigation_id || '',
      type: item.type || 'other',
      subject: item.subject || '',
      body: item.body || '',
      to_recipients: item.to_recipients || '',
      cc_recipients: item.cc_recipients || '',
      direction: item.direction || 'outgoing',
    });
    setIsFormOpen(true);
  };

  const openNew = () => {
    setEditingId(null);
    setFormData({ ...emptyForm });
    setIsFormOpen(true);
  };

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const filtered = items.filter(i => {
    if (filterInv !== 'all' && i.investigation_id !== filterInv) return false;
    if (filterDir !== 'all' && i.direction !== filterDir) return false;
    return true;
  });

  const stats = {
    total: items.length,
    outgoing: items.filter(i => i.direction === 'outgoing').length,
    incoming: items.filter(i => i.direction === 'incoming').length,
    draft: items.filter(i => i.status === 'draft').length,
  };

  return (
    <div className="max-w-5xl" data-testid="correspondence-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>Correspondence</h1>
          <p className="text-sm text-slate-500 mt-1">Track and manage investigation communications</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant="outline" className="rounded-lg" onClick={() => setShowConfig(!showConfig)} data-testid="email-config-btn">
              <Gear size={16} className="mr-2" /> Email Setup
            </Button>
          )}
          {canEdit && (
            <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg" onClick={openNew} data-testid="new-correspondence-btn">
              <Plus size={16} className="mr-2" /> New
            </Button>
          )}
        </div>
      </div>

      {/* Email Config Banner */}
      {showConfig && emailConfig && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6" data-testid="email-config-panel">
          <h2 className="text-sm font-bold text-slate-800 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Gear size={14} className="text-slate-500" /> Email Provider Configuration
          </h2>
          <div className="flex items-center gap-3 mb-4">
            <div className={`px-3 py-1.5 rounded-lg text-sm font-medium ${emailConfig.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
              {emailConfig.is_active ? <CheckCircle size={14} className="inline mr-1" /> : <WarningCircle size={14} className="inline mr-1" />}
              {emailConfig.is_active ? `Active: ${emailConfig.provider.toUpperCase()}` : 'Log Only (emails not actually sent)'}
            </div>
            {emailConfig.smtp_from && (
              <div className="px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-50 text-blue-800">
                <EnvelopeSimple size={14} className="inline mr-1" />
                Sending as: {emailConfig.smtp_from}
              </div>
            )}
          </div>
          <div className="text-sm text-slate-600 space-y-2">
            <p className="font-medium text-slate-800">To enable email sending, set these environment variables:</p>
            {Object.entries(emailConfig.setup_instructions || {}).map(([provider, instructions]) => (
              <div key={provider} className="flex gap-2 text-xs bg-slate-50 rounded-lg p-2">
                <span className="font-mono font-bold text-blue-700 shrink-0 w-20">{provider}:</span>
                <span className="text-slate-600">{instructions}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        {[
          { label: 'Total', count: stats.total, color: 'bg-slate-800 text-white' },
          { label: 'Outgoing', count: stats.outgoing, color: 'bg-blue-50 text-blue-800' },
          { label: 'Incoming', count: stats.incoming, color: 'bg-green-50 text-green-800' },
          { label: 'Drafts', count: stats.draft, color: 'bg-amber-50 text-amber-800' },
        ].map(s => (
          <div key={s.label} className={`rounded-xl p-3 text-center ${s.color}`} data-testid={`stat-${s.label.toLowerCase()}`}>
            <div className="text-xl font-bold" style={{ fontFamily: 'Outfit' }}>{s.count}</div>
            <div className="text-xs opacity-80">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <Select value={filterInv} onValueChange={setFilterInv}>
          <SelectTrigger className="rounded-lg w-64 text-sm" data-testid="filter-investigation">
            <SelectValue placeholder="All Investigations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Investigations</SelectItem>
            {investigations.map(inv => (
              <SelectItem key={inv.id} value={inv.id}>{inv.investigation_number}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex gap-1">
          {['all', 'outgoing', 'incoming'].map(dir => (
            <button
              key={dir}
              onClick={() => setFilterDir(dir)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                filterDir === dir ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`filter-${dir}`}
            >
              {dir === 'all' ? 'All' : dir.charAt(0).toUpperCase() + dir.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-xl p-12 text-center text-slate-500 border">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center border border-slate-200">
            <EnvelopeSimple size={40} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">{items.length === 0 ? 'No correspondence yet' : 'No matches for current filters'}</p>
          </div>
        ) : (
          filtered.map(item => (
            <CorrCard key={item.id} item={item} onEdit={handleEdit} onSend={handleSend} onDelete={handleDelete} canEdit={canEdit} />
          ))
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>
              {editingId ? 'Edit Correspondence' : 'New Correspondence'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="correspondence-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold text-slate-700 uppercase">Investigation *</Label>
                <Select value={formData.investigation_id} onValueChange={val => updateField('investigation_id', val)}>
                  <SelectTrigger className="mt-1 rounded-lg" data-testid="form-corr-investigation">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {investigations.map(inv => (
                      <SelectItem key={inv.id} value={inv.id}>
                        {inv.sector === 'aviation' ? <AirplaneTilt size={12} className="inline mr-1" /> : <Anchor size={12} className="inline mr-1" />}
                        {inv.investigation_number}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-700 uppercase">Type</Label>
                <Select value={formData.type} onValueChange={val => updateField('type', val)}>
                  <SelectTrigger className="mt-1 rounded-lg" data-testid="form-corr-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-700 uppercase">Direction</Label>
                <Select value={formData.direction} onValueChange={val => updateField('direction', val)}>
                  <SelectTrigger className="mt-1 rounded-lg" data-testid="form-corr-direction">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outgoing">Outgoing</SelectItem>
                    <SelectItem value="incoming">Incoming</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold text-slate-700 uppercase">To Recipients</Label>
                <Input value={formData.to_recipients} onChange={e => updateField('to_recipients', e.target.value)} className="mt-1 rounded-lg" placeholder="email@example.com" data-testid="form-corr-to" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs font-bold text-slate-700 uppercase">CC Recipients</Label>
                <Input value={formData.cc_recipients} onChange={e => updateField('cc_recipients', e.target.value)} className="mt-1 rounded-lg" placeholder="cc@example.com (optional)" data-testid="form-corr-cc" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold text-slate-700 uppercase">Subject *</Label>
              <Input value={formData.subject} onChange={e => updateField('subject', e.target.value)} className="mt-1 rounded-lg" placeholder="Subject line..." data-testid="form-corr-subject" required />
            </div>
            <div>
              <Label className="text-xs font-bold text-slate-700 uppercase">Body</Label>
              <Textarea value={formData.body} onChange={e => updateField('body', e.target.value)} className="mt-1 rounded-lg min-h-[150px]" placeholder="Message body..." data-testid="form-corr-body" />
            </div>
            <div className="flex gap-3 pt-4 border-t border-slate-100">
              <Button type="submit" className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg" data-testid="submit-correspondence">
                {editingId ? 'Update' : 'Save as Draft'}
              </Button>
              <Button type="button" variant="outline" className="rounded-lg" onClick={() => setIsFormOpen(false)}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
